import {
  getGridStringOperators,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
} from '@mui/x-data-grid-pro';
import { ActivityModel, EventModel, Page } from '../../../types';
import { useCallback, useMemo, useRef, useState } from 'react';
import AvatarChip from '../../shared/avatar-chip';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { exportCpanelLogs, getCpanelLogs, UserKeys } from '../../api/users-api';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import { handleErrors } from '../../utils/errors';
import ChangeLogDialog from '../../shared/change-log-dialog';
import CPanelLogsGridToolbar from './cPanelLogs-toolbar';
import LogDetailsDrawer from './log-details-drawer';
import { eventNames } from '../../shared/event-names';
import dayjs, { Dayjs } from 'dayjs';
import { ActorFilter } from './filter-components/actor-filter';
import { IPFilter } from './filter-components/ip-filter';
import { EventFilter } from './filter-components/event-filter';
import { DateFilter } from './filter-components/date-filter';
import { DateRange } from '@mui/x-date-pickers-pro';
import { createGridOverlays } from '../grid-overlay/no-data-overlay-helpers';
import useGridColumns from '../../shared/hooks/use-grid-columns-latest';
import SharedDataGridServer from '../../shared/shared-data-grid-server';
import { useUserSettings } from '../../utils/use-user-settings';
import { useToolbar } from '../../shared/shared-data-grid-toolbar-helper';
import { isFeatureEnabled } from '../../utils/feature-flags';
import LogDetailsDrawerOld from './log-details-drawer-old';
import FindInPage from '@mui/icons-material/FindInPage';

interface CPanelLogsGridProps {
  administrationRoles: string[];
  loggedInUser: string;
  changeLogRoles: string[];
}

export interface cPanelLogSearchForm {
  dateRange?: DateRange<Dayjs>;
  startDate: number;
  endDate: number;
  actor: string;
  ip: string;
  eventName: string;
  orgUnit: string;
}

export default function CPanelLogsGrid({
  administrationRoles,
  loggedInUser,
  changeLogRoles,
}: CPanelLogsGridProps) {
  const { createEvent } = useAnalytics();
  const { viewType, features } = useUserSettings();
  const uiEnhancementFeatureEnabled = isFeatureEnabled(
    'uiEnhancementPhase2',
    features
  );
  const [selectedLog, setSelectedLog] = useState<ActivityModel | null>(null);
  const [openLogDetails, setOpenLogDetails] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const beginDate = useRef(
    dayjs().subtract(180, 'day').startOf('day').valueOf()
  );

  const columns: GridColDef<ActivityModel>[] = [
    {
      field: 'time',
      headerName: 'Date',
      filterable: true,
      sortable: false,
      pinnable: false,
      minWidth: 180,
      type: 'date',
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'equals')
        .map((operator) => {
          return {
            ...operator,
            InputComponent: operator.InputComponent ? DateFilter : undefined,
          };
        }),
      valueGetter: (_value, row) => {
        if (row.id && row.id.time) {
          const date = new Date(row.id.time.value);
          return date;
        } else {
          return 'N/A';
        }
      },
      valueFormatter: (date: Date | string) => {
        if (date instanceof Date) {
          return date.toLocaleString();
        } else {
          return 'N/A';
        }
      },
    },
    {
      field: 'events',
      headerName: 'Event Name',
      filterable: true,
      sortable: false,
      pinnable: false,
      minWidth: 200,
      type: 'singleSelect',
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'equals')
        .map((operator) => {
          return {
            ...operator,
            InputComponent: operator.InputComponent ? EventFilter : undefined,
          };
        }),
      valueGetter: (_value: EventModel, row) => {
        const name = row.events[0].name;
        const event = eventNames.find((event) => event.value === name);
        return event ? event.label : name;
      },
    },
    {
      field: 'action',
      headerName: 'Description',
      filterable: false,
      sortable: false,
      pinnable: false,
      display: 'flex',
      disableColumnMenu: true,
      flex: 1,
      valueGetter: (_value: EventModel[], row) => {
        const name = row.events[0].name;
        const parameters = row.events[0].parameters || [];
        const event = eventNames.find((event) => event.value === name);
        if (!event) return 'N/A';
        if (!event.messageFormat) return 'N/A';

        const formattedParameters = parameters.map((param) => {
          return {
            [param.name]: param.value,
          };
        });
        const parametersObject = Object.assign({}, ...formattedParameters);
        return event.messageFormat(parametersObject as never);
      },
    },
    {
      field: 'email',
      headerName: 'Actor',
      filterable: true,
      sortable: false,
      pinnable: false,
      minWidth: 170,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'equals')
        .map((operator) => {
          return {
            ...operator,
            InputComponent: operator.InputComponent ? ActorFilter : undefined,
          };
        }),
      renderCell: (params) => {
        if (params.row.actor.email) {
          return <AvatarChip email={params.row.actor.email} type="User" />;
        } else {
          return 'N/A';
        }
      },
    },
    {
      field: 'ip',
      headerName: 'IP Address',
      filterable: true,
      sortable: false,
      pinnable: false,
      minWidth: 200,
      type: 'string',
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'equals')
        .map((operator) => {
          return {
            ...operator,
            InputComponent: operator.InputComponent ? IPFilter : undefined,
          };
        }),
      valueGetter: (_value, row) => {
        if (row.ipAddress) {
          return row.ipAddress;
        } else {
          return 'N/A';
        }
      },
    },
  ];

  const { initialState: storedInitialState, computedColumns } = useGridColumns(
    columns,
    'cpanelLogsGridStates'
  );

  const initialState = useMemo(() => {
    if (!storedInitialState?.filter?.filterModel) {
      return storedInitialState;
    }

    const newFilterModel = { ...storedInitialState.filter.filterModel };
    newFilterModel.items = newFilterModel.items.map((item) => {
      if (item.field === 'time' && Array.isArray(item.value)) {
        return {
          ...item,
          value: [
            item.value[0] ? dayjs(item.value[0]) : null,
            item.value[1] ? dayjs(item.value[1]) : null,
          ],
        };
      }
      return item;
    });

    return {
      ...storedInitialState,
      filter: {
        ...storedInitialState.filter,
        filterModel: newFilterModel,
      },
    };
  }, [storedInitialState]);

  const findFilterValue = (key: string, part?: 'start' | 'end'): string => {
    if (!initialState) return '';

    const filterItem = initialState.filter?.filterModel?.items.find(
      (item) => item.field === key
    );

    if (!filterItem?.value) return '';

    if (key === 'time' && Array.isArray(filterItem.value)) {
      const dateRange = filterItem.value as (dayjs.Dayjs | null)[];
      if (part === 'start' && dateRange[0])
        return dayjs(dateRange[0]).startOf('day').valueOf().toString();
      if (part === 'end' && dateRange[1])
        return dayjs(dateRange[1]).endOf('day').valueOf().toString();
      return '';
    }

    return (filterItem.value as string) ?? '';
  };

  const currentDate = useRef(dayjs().endOf('day').valueOf());
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    startDate: number;
    endDate: number;
    actor: string;
    ip: string;
    eventName: string;
    orgUnit: string;
  }>({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 50,
    page: 0,
    cursor: null,
    startDate: Number(findFilterValue('time', 'start')) || beginDate.current,
    endDate: Number(findFilterValue('time', 'end')) || currentDate.current,
    actor: findFilterValue('email'),
    ip: findFilterValue('ip'),
    eventName: findFilterValue('events'),
    orgUnit: '',
  });

  const {
    data: cPanelLogs,
    isLoading: cPanelLogsLoading,
    isFetching: cPanelLogsFetching,
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      UserKeys.CPanelLogs,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.actor,
      paginationModel.eventName,
      paginationModel.orgUnit,
      paginationModel.ip,
      paginationModel.startDate,
      paginationModel.endDate,
    ],
    queryFn: () => {
      const object: Page<ActivityModel[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      return getCpanelLogs(object, { ...paginationModel });
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage:
        'There was an issue loading the cPanel logs. Please refresh and try again.',
    },
  });

  function isValidIP(ip: string) {
    const trimmedIp = ip.trim();
    const ipv4Pattern =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipv6Pattern =
      /^(([0-9a-fA-F]{1,4}:){7}([0-9a-fA-F]{1,4}|:)|(([0-9a-fA-F]{1,4}:){1,6}|:):(([0-9a-fA-F]{1,4}:){1,6}|:)|::|(([0-9a-fA-F]{1,4}:){1,5}|:):(([0-9a-fA-F]{1,4}:){1,5}|:):|(([0-9a-fA-F]{1,4}:){1,4}|:):(([0-9a-fA-F]{1,4}:){1,4}|:):|(([0-9a-fA-F]{1,4}:){1,3}|:):(([0-9a-fA-F]{1,4}:){1,3}|:):|(([0-9a-fA-F]{1,4}:){1,2}|:):(([0-9a-fA-F]{1,4}:){1,2}|:):|([0-9a-fA-F]{1,4}|:):([0-9a-fA-F]{1,4}|:):)$/;
    return ipv4Pattern.test(trimmedIp) || ipv6Pattern.test(trimmedIp);
  }

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      const object = { ...paginationModel };
      const hasTimeFilter = filterModel.items.find(
        (filter) => filter.field === 'time'
      )?.value;
      const hasEmailFilter = filterModel.items.find(
        (filter) => filter.field === 'email'
      )?.value;
      const hasEventsFilter = filterModel.items.find(
        (filter) => filter.field === 'events'
      )?.value;
      const hasIpFilter = filterModel.items.find(
        (filter) => filter.field === 'ip'
      )?.value;

      if (hasTimeFilter) {
        const timeFilterValue = filterModel.items.find(
          (filter) => filter.field === 'time'
        )!.value as DateRange<dayjs.Dayjs> | (string | null)[];

        const start = timeFilterValue[0] ? dayjs(timeFilterValue[0]) : null;
        const end = timeFilterValue[1] ? dayjs(timeFilterValue[1]) : null;

        object.startDate =
          start && start.isValid()
            ? start.startOf('day').valueOf()
            : object.startDate;

        object.endDate =
          end && end.isValid() ? end.endOf('day').valueOf() : object.endDate;
      } else {
        object.startDate = beginDate.current;
        object.endDate = currentDate.current;
      }

      if (hasEmailFilter) {
        const actorFilter = filterModel.items.find(
          (filter) => filter.field === 'email'
        )!.value as string;
        object.actor = actorFilter;
      } else if (object.actor !== '') {
        object.actor = '';
      }

      if (hasEventsFilter) {
        const eventsFilter = filterModel.items.find(
          (filter) => filter.field === 'events'
        )!.value as string;
        object.eventName = eventsFilter;
      } else if (object.eventName !== '') {
        object.eventName = '';
      }

      if (hasIpFilter) {
        const ipFilter = filterModel.items.find(
          (filter) => filter.field === 'ip'
        )!.value as string;
        if (!isValidIP(ipFilter)) {
          return;
        } else {
          object.ip = ipFilter;
        }
      } else if (object.ip !== '') {
        object.ip = '';
      }

      object.cursor = null;
      object.page = 0;

      if (JSON.stringify(object) !== JSON.stringify(paginationModel)) {
        setPaginationModel(object);
      }
    },
    [paginationModel]
  );

  const { mutate, isPending: isExporting } = useMutation({
    mutationFn: (data: cPanelLogSearchForm) =>
      exportCpanelLogs(data, changeLogMessage),
    onSuccess: (data) => {
      window.open(
        `https://docs.google.com/a/${
          loggedInUser.split('@')[1]
        }/spreadsheet/ccc?key=${data}#gid=0`,
        '_blank',
        'noopener, noreferrer'
      );
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue exporting the cPanel log search results. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.report_center.cPanel_Logs.exported_cPanel_logs, {
        module: ModuleList.modules.reportCenter.name,
        section: ModuleList.modules.reportCenter.sections.cPanelLogs,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleExportMutate() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.ExportAppLogs.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate({
        dateRange: [
          dayjs(paginationModel.startDate),
          dayjs(paginationModel.endDate),
        ],
        startDate: paginationModel.startDate,
        endDate: paginationModel.endDate,
        actor: paginationModel.actor,
        ip: paginationModel.ip,
        eventName: paginationModel.eventName,
        orgUnit: paginationModel.orgUnit,
      });
    }
  }
  const toolbarProps = useMemo(() => {
    return {
      administrationRoles: administrationRoles,
      handleExportMutate: handleExportMutate,
      searchedLogs: cPanelLogs?.results || [],
      isExporting: isExporting,
      isSearching: cPanelLogsLoading || cPanelLogsFetching,
      setPaginationModel: setPaginationModel,
      beginDate: beginDate.current,
      currentDate: currentDate.current,
    };
  }, [
    administrationRoles,
    handleExportMutate,
    cPanelLogs?.results,
    isExporting,
    cPanelLogsLoading,
    cPanelLogsFetching,
  ]);
  return (
    <>
      <SharedDataGridServer
        data={cPanelLogs}
        slots={{
          toolbar: useToolbar(CPanelLogsGridToolbar, toolbarProps),
          ...createGridOverlays(
            !cPanelLogs ? 'Search Above' : 'Logs',
            <FindInPage />,
            'Select any optional filters you would like to search by.',
            'Try changing your search terms.'
          ),
        }}
        initialState={
          initialState || {
            pagination: {
              paginationModel: {
                pageSize: 50,
                page: 0,
              },
              rowCount: 0,
            },
          }
        }
        filterOperator={[GridLogicOperator.And]}
        filterPanelSx={{ minWidth: 800 }}
        columns={computedColumns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        getRowId={(row) => row.etag}
        paginationModel={{
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
        }}
        onPaginationModelChange={(newModel) => {
          if (cPanelLogsLoading || cPanelLogsFetching) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel((prev) => ({
              ...prev,
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
            }));
          } else {
            setPaginationModel((prev) => ({
              ...prev,
              page: newModel.page,
              pageSize: newModel.pageSize,
              cursor: cPanelLogs?.cursor || '',
            }));
          }
        }}
        onFilterModelChange={onFilterChange}
        loading={cPanelLogsLoading || cPanelLogsFetching}
        disableRowSelectionOnClick
        onRowClick={(row) => {
          const selectedLog = cPanelLogs?.results.find(
            (log) => log.etag === row.id
          ) as ActivityModel;
          setSelectedLog(selectedLog);
          setOpenLogDetails(true);
        }}
        gridKey={viewType.cpanelLogsGridStates?.activeGridState || 'default'}
      />
      {openChangeLogDialog ? (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            mutate({
              dateRange: [
                dayjs(paginationModel.startDate),
                dayjs(paginationModel.endDate),
              ],
              startDate: paginationModel.startDate,
              endDate: paginationModel.endDate,
              actor: paginationModel.actor,
              ip: paginationModel.ip,
              eventName: paginationModel.eventName,
              orgUnit: paginationModel.orgUnit,
            });
          }}
          mutateLoading={isExporting}
        />
      ) : null}
      {uiEnhancementFeatureEnabled ? (
        <LogDetailsDrawer
          open={openLogDetails}
          handleClose={() => setOpenLogDetails(false)}
          selectedLog={selectedLog}
        />
      ) : (
        <LogDetailsDrawerOld
          open={openLogDetails}
          handleClose={() => setOpenLogDetails(false)}
          selectedLog={selectedLog}
        />
      )}
    </>
  );
}
